import { observer } from 'mobx-react';
import React, { useRef } from 'react';
import { Mention, MentionsInput } from 'react-mentions'

import { COLORS, ICONS } from "shared/constants";
import { useStore } from "shared/hooks";
import { UserModel } from "shared/models";
import { ICommentMentionModel } from "shared/models/CommentMentionModel";
import { ApplicationModule } from "shared/modules";
import { usersQuery } from "shared/queries";
import { UiAvatar, UiIcon } from "shared/uikit";
import { userPreviewName } from "shared/utilities";

import { Store } from "../../Store";
import { COMMENT_FILE_ACCEPT, COMMENT_MAX_FILES, validateCommentFiles } from '../../fileValidation';
import { CCommentFiles } from '../CCommentFiles';

import './index.scss';

type PropsType = {
    id?: number | null,
    commentId: number | null,
    value?: string,
    files?: string[],
    allowFiles?: boolean,
    onSubmit?: () => void
}

export const CCommentSave = observer(({
    id = null, commentId, onSubmit, value, files = [], allowFiles = false
}: PropsType) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const store = useStore(() => ({
        value: value || '',
        commentMentions: [] as ICommentMentionModel[],
        isLoading: false,
        files: [...files],
        newFiles: [] as File[],
        error: '',
    }))

    const hasContent = !!store.value.trim() || (allowFiles && (store.files.length > 0 || store.newFiles.length > 0));

    const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(event.target.files || []);
        event.target.value = '';
        if (store.isLoading || selected.length === 0) {
            return;
        }
        const nextFiles = [...store.newFiles, ...selected];
        const error = validateCommentFiles(nextFiles, store.files.length);
        store.set('error', error || '');
        if (!error) {
            store.set('newFiles', nextFiles);
        }
    };

    const handleSubmit = async () => {
        if (store.isLoading || !hasContent) {
            return;
        }
        const error = allowFiles ? validateCommentFiles(store.newFiles, store.files.length) : null;
        if (error) {
            store.set('error', error);
            return;
        }
        const commentMentions = store.commentMentions.filter(commentMention => {
            if (!commentMention?.userId || !commentMention?.userName) {
                return false;
            }
            return store.value.indexOf(`${commentMention.userId}`) > -1 && store.value.indexOf(commentMention.userName) > -1;
        });

        let value = `${store.value}`;
        commentMentions.forEach(commentMention => {
            if (commentMention.userName) {
                value = value.replace(commentMention.userName, userPreviewName(commentMention.userName));
            }
        })
        store.set('error', '');
        store.set("isLoading", true);
        try {
            const { isSuccess, data, errorMessage, errorData } = await Store.saveComment({
                id,
                value,
                commentId,
                commentMentions,
                ...(allowFiles ? {
                    newFiles: store.newFiles,
                    clearFiles: files.filter(file => !store.files.includes(file)),
                } : {}),
            });
            if (!isSuccess || !data) {
                const validationMessage = Array.from(new Set(Object.values(errorData || {}).flat())).join(' ');
                store.set('error', validationMessage || errorMessage || 'Не удалось сохранить комментарий. Попробуйте ещё раз.');
                return;
            }
            store.set('value', '');
            store.set('commentMentions', []);
            store.set('files', []);
            store.set('newFiles', []);
            onSubmit?.();
        } catch {
            store.set('error', 'Не удалось сохранить комментарий. Попробуйте ещё раз.');
        } finally {
            store.set('isLoading', false);
        }
    }

    return (
        <>
            <div className="c-comment-save">
                {!id && (
                    <div className="c-comment-save__image">
                        <UiAvatar size={48} image={ApplicationModule.user.imageAvatar}/>
                    </div>
                )}
                <div className="c-comment-save__body">
                    <div className={`c-comment-save__inner ${allowFiles ? 'c-comment-save__inner--with-files' : ''}`}>
                        <MentionsInput
                            className={'c-comment-save-control'}
                            value={store.value}
                            disabled={store.isLoading}
                            allowSpaceInQuery
                            onKeyDown={(e) => {
                                if (e.keyCode === 13 && e.ctrlKey) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    store.set('value', store.value + "\n");
                                    return false;
                                }
                                if (e.keyCode === 13 && !e.shiftKey) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSubmit();
                                    return false;
                                }
                            }}
                            onChange={(e) => {
                                store.set("value", e.target.value);
                            }}
                            allowSuggestionsAboveCursor
                        >
                            <Mention
                                trigger="@"
                                data={async (query, callback) => {
                                    const response = await usersQuery({
                                        query,
                                        limit: 8
                                    });

                                    if (!response.isSuccess || !response.data) {
                                        return [];
                                    }

                                    const data = response.data.items.map(item => {
                                        const user = new UserModel(item);
                                        return {
                                            id: user.id,
                                            display: user.name
                                        }
                                    });

                                    callback(data);
                                    return data;
                                }}
                                markup={'<a target="_blank" href="/user/__id__">__display__</a>'}
                                onAdd={(userId, userName) => {
                                    if (!store.commentMentions.find(commentMention => commentMention.userId === userId)) {
                                        store.set("commentMentions", [...store.commentMentions, { userId, userName }]);
                                    }
                                }}
                                displayTransform={(_, display) => {
                                    return userPreviewName(display);
                                }}
                            />
                        </MentionsInput>
                        <div className="c-comment-save__placeholder">
                            Напишите комментарий
                        </div>
                        <div className="c-comment-save__actions">
                            {allowFiles && (
                                <>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        hidden
                                        multiple
                                        accept={COMMENT_FILE_ACCEPT}
                                        disabled={store.isLoading || store.files.length + store.newFiles.length >= COMMENT_MAX_FILES}
                                        onChange={handleFilesChange}
                                    />
                                    <button
                                        type="button"
                                        className="c-comment-save__attach"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={store.isLoading || store.files.length + store.newFiles.length >= COMMENT_MAX_FILES}
                                        title={`Прикрепить файлы (${store.files.length + store.newFiles.length}/${COMMENT_MAX_FILES}). До 20 МБ каждый, до 100 МБ суммарно. JPG, JPEG, PNG, WEBP, GIF, DOC, DOCX, XLS, XLSX, PPT, PPTX.`}
                                    >
                                        <UiIcon icon={ICONS.ATTACHMENT} color={COLORS.GRAY_4A} size={24}/>
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                className="c-comment-save__send"
                                onClick={handleSubmit}
                                disabled={store.isLoading || !hasContent}
                            >
                                <UiIcon icon={ICONS.SEND} color={COLORS.GRAY_4A} size={24}/>
                            </button>
                        </div>
                    </div>
                    {allowFiles && (
                        <CCommentFiles
                            files={store.files}
                            newFiles={store.newFiles}
                            disabled={store.isLoading}
                            onChangeFiles={files => store.set('files', files)}
                            onChangeNewFiles={files => store.set('newFiles', files)}
                            onError={error => store.set('error', error)}
                        />
                    )}
                    {store.error && <div className="c-comment-save__error">{store.error}</div>}
                </div>
            </div>
        </>
    );
})
